/* ==========================================================================
   Airah Bespoke — Bed Configurator (presentation layer)

   WHAT THIS DOES
   It arranges the YMQ Product Options app's own elements into clear, numbered
   sections so a customer can see the whole specification on arrival, instead
   of discovering options only after choosing a size.

   WHAT THIS DELIBERATELY DOES NOT DO
   It never creates, renames, removes or reorders an option's values, and it
   never touches the Shopify variant structure. It moves the app's real
   elements; it does not copy them. Selection, surcharges, price updates,
   conditional logic, validation, the fabric hover enlargement and add-to-cart
   therefore remain entirely the app's own behaviour.

   HOW DEPENDENCIES ARE HONOURED
   The app remains the single source of truth for what may be chosen. Options
   it disables stay disabled, shown in a quiet "awaiting" state with the reason.
   Groups that are alternatives of one another (a fabric colour grid per family,
   a mattress group per bed size) are merged into one section, and the app
   decides which one is on show. No combination can be selected that the app
   would refuse.
   ========================================================================== */

(function () {
  'use strict';

  var BOX = '.ymq-options-box';
  var SHELL = 'ac-shell';
  var built = false;
  var syncQueued = false;
  var observer = null;

  /* ---------------------------------------------------------------- helpers */

  function boxes(root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(BOX));
  }

  function labelOf(box) {
    return (box.getAttribute('ymq-option-label') || '').trim();
  }

  function isHidden(el) {
    return window.getComputedStyle(el).display === 'none';
  }

  function controls(box) {
    return Array.prototype.slice.call(
      box.querySelectorAll('.ymq-option-rc-input, select.ymq-option-native-select, input, textarea, select')
    );
  }

  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  }

  function pad(n) {
    return (n < 10 ? '0' : '') + n;
  }

  /* Frame an option's own label in a sentence, without renaming it: only a
     leading "Select"/"Choose" is dropped so the hint does not read twice. */
  function forSentence(text) {
    if (!text) return 'the first option';
    return text.replace(/^\s*(select|choose)\s+/i, '').trim() || text.trim();
  }

  /* --------------------------------------------------------------- grouping */

  /* Build sections from the live DOM. A box is an "alternative" when another
     box shares its label (the per-size mattress groups) or when its label is
     one of the values of a dropdown on the page (a fabric family's colour
     grid). Alternatives are merged into their parent's section; everything
     else becomes a section of its own. Entirely data-driven: adding a fabric
     family or a size in the app needs no change here. */
  function plan(list) {
    var byLabel = {};
    list.forEach(function (box) {
      var l = labelOf(box);
      if (!l) return;
      (byLabel[l] = byLabel[l] || []).push(box);
    });

    /* Which labels are the alternatives offered by which parent dropdown? */
    var ownerOf = {};
    list.forEach(function (box) {
      var select = box.querySelector('select.ymq-option-native-select');
      if (!select) return;
      var matched = [];
      Array.prototype.forEach.call(select.options, function (opt) {
        var v = (opt.value || '').trim();
        if (v && byLabel[v] && byLabel[v].indexOf(box) === -1) matched.push(v);
      });
      if (matched.length >= 2) {
        matched.forEach(function (v) { ownerOf[v] = labelOf(box); });
      }
    });

    var sections = [];
    var byKey = {};

    list.forEach(function (box) {
      var label = labelOf(box);

      /* A fabric colour grid joins the section of the dropdown offering it.
         These are the swatch grids whose imagery IS the choice, so they are
         marked to keep their swatch presentation. */
      var ownerLabel = ownerOf[label];
      if (ownerLabel && byKey[ownerLabel]) {
        box.classList.add('ac-fabric');
        byKey[ownerLabel].alternatives.push(box);
        return;
      }

      /* Groups sharing a label (a mattress group per size) merge into one. */
      if (byKey[label]) {
        byKey[label].alternatives.push(box);
        byKey[label].merged = true;
        return;
      }

      var section = {
        title: label,
        primary: box,
        alternatives: [],
        /* True once a second box with the SAME label joins: then every box in
           the section is an alternative and the app chooses which is shown. */
        merged: false
      };

      byKey[label] = section;
      sections.push(section);
    });

    return sections;
  }

  /* ------------------------------------------------------------ chip proxies */

  /* Replace a native <select> visually with a row of chips. The select stays
     in the DOM and remains the value the app reads; chips only drive it. */
  function proxySelect(select, section) {
    if (select.getAttribute('data-ac-proxied') === '1') return;

    var wrapper = select.closest('.ymq-option-select-wrapper') || select.parentElement;
    var row = el('div', 'ac-chips');
    row.setAttribute('role', 'group');
    if (section && section.title) row.setAttribute('aria-label', section.title);

    var chips = [];

    Array.prototype.forEach.call(select.options, function (opt) {
      if (!opt.value || opt.classList.contains('ymq-option-select-placeholder')) return;

      var chip = el('button', 'ac-chip');
      chip.type = 'button';
      chip.setAttribute('aria-pressed', 'false');
      chip.setAttribute('data-ac-value', opt.value);

      /* Name and surcharge are read from the app's own markup — never typed. */
      var nameNode = opt.querySelector ? opt.querySelector('.ymq-option-value') : null;
      var name = nameNode ? nameNode.textContent.trim() : (opt.textContent || opt.value).trim();
      chip.appendChild(el('span', 'ac-chip__label', name || opt.value));

      var priceNode = opt.querySelector ? opt.querySelector('.ymq-option-value-price') : null;
      var priceText = priceNode ? priceNode.textContent.replace(/\s+/g, ' ').trim() : '';
      if (priceText) chip.appendChild(el('span', 'ac-chip__price', priceText));

      chip.addEventListener('click', function () {
        if (select.disabled || chip.disabled) return;
        select.value = opt.value;
        select.dispatchEvent(new Event('input', { bubbles: true }));
        select.dispatchEvent(new Event('change', { bubbles: true }));
        queueSync();
      });

      chips.push(chip);
      row.appendChild(chip);
    });

    if (!chips.length) return;

    /* Long names read better stacked than as a row of wide chips. */
    var longest = chips.reduce(function (max, chip) {
      var label = chip.querySelector('.ac-chip__label');
      return Math.max(max, label ? label.textContent.trim().length : 0);
    }, 0);
    if (longest > 22) row.classList.add('ac-chips--stacked');

    wrapper.parentNode.insertBefore(row, wrapper);
    wrapper.classList.add('ac-proxied');
    select.setAttribute('aria-hidden', 'true');
    select.setAttribute('tabindex', '-1');
    select.setAttribute('data-ac-proxied', '1');
    select._acChips = chips;

    select.addEventListener('change', queueSync);
  }

  /* ------------------------------------------------------- textual swatches */

  /* Radio groups whose tiles carry a product thumbnail (bed sizes, bases,
     TV lift, per-size mattress menus) read as product cards, not selectors.
     Their imagery repeats the gallery photo and adds nothing to the choice,
     so those groups are presented as text chips instead — still the app's
     own radio inputs and labels, only the thumbnail is not shown. Fabric
     grids (marked ac-fabric in plan()) keep their swatches: there the image
     IS the information. Entirely data-driven; no group names are hard-coded. */
  /* Owner's presentation choice (17 Sep 2026): mattress, base and TV groups
     keep their imagery — the photo genuinely differentiates those products —
     shown as a clean media row (thumbnail, name, price), never as the app's
     picture-card tiles. Everything else non-fabric is text only. */
  var MEDIA_GROUPS = /mattress|\bbase\b|\btv\b/i;

  function textualise(box) {
    if (box.classList.contains('ac-fabric')) return;
    if (box.getAttribute('data-ac-textual') === '1') return;

    var items = box.querySelectorAll('.ymq-option-rc-input + .ymq-option-swatches-item');
    if (!items.length) return;

    box.setAttribute('data-ac-textual', '1');
    box.classList.add('ac-textual');

    if (MEDIA_GROUPS.test(labelOf(box)) && box.querySelector('.ymq-option-swatches-image')) {
      box.classList.add('ac-media', 'ac-textual--stacked');
      return;
    }

    /* Long value names (mattress models) read better as a stacked list than
       as a row of wide chips — the same rule the dropdown chips follow. */
    var longest = 0;
    Array.prototype.forEach.call(
      box.querySelectorAll('.ymq-option-swatches-label'),
      function (label) {
        var clone = label.cloneNode(true);
        var price = clone.querySelector('.ymq-option-value-price');
        if (price) price.parentNode.removeChild(price);
        longest = Math.max(longest, clone.textContent.trim().length);
      }
    );
    if (longest > 22) box.classList.add('ac-textual--stacked');
  }

  function syncChips(select) {
    var chips = select._acChips;
    if (!chips) return;
    chips.forEach(function (chip) {
      var on = select.value === chip.getAttribute('data-ac-value');
      chip.setAttribute('aria-pressed', on ? 'true' : 'false');
      chip.disabled = !!select.disabled;
    });
  }

  /* ----------------------------------------------------------------- render */

  function render(list) {
    var anchor = list[0];
    var container = anchor.parentNode;
    var shell = el('div', SHELL);
    shell.setAttribute('data-ac-configurator', '');

    container.insertBefore(shell, anchor);

    var sections = plan(list);

    sections.forEach(function (section) {
      var node = el('section', 'ac-step');
      node.setAttribute('data-ac-key', section.title || 'option');

      var head = el('div', 'ac-step__head');
      head.appendChild(el('span', 'ac-step__num', ''));
      head.appendChild(el('h3', 'ac-step__title', section.title || ''));
      head.appendChild(el('span', 'ac-step__value', ''));

      var body = el('div', 'ac-step__body');
      var all = [section.primary].concat(section.alternatives);
      all.forEach(function (box) { body.appendChild(box); });

      var hint = el('p', 'ac-step__hint', '');

      node.appendChild(head);
      node.appendChild(body);
      node.appendChild(hint);
      shell.appendChild(node);

      section.node = node;
      section.all = all;
    });

    return { shell: shell, sections: sections };
  }

  /* ------------------------------------------------------------------ state */

  var state = null;

  function sync() {
    if (!state) return;

    var firstTitle = state.sections.length ? forSentence(state.sections[0].title) : 'an option';
    var shown = 0;

    state.sections.forEach(function (section) {
      /* The section's own control is gated on an earlier choice rather than
         being one alternative among several: reveal it, so the customer sees
         the choice exists. Where every box in the section IS an alternative
         (a mattress group per size), the app decides which one is shown. */
      if (!section.merged) {
        section.primary.classList.add('ac-reveal');
      } else {
        section.all.forEach(function (b) { b.classList.remove('ac-reveal'); });
      }

      section.all.forEach(textualise);

      var visible = section.all.filter(function (b) {
        return b.classList.contains('ac-reveal') || !isHidden(b);
      });

      /* Build chip rows for any dropdown now on show. */
      visible.forEach(function (box) {
        Array.prototype.forEach.call(
          box.querySelectorAll('select.ymq-option-native-select'),
          function (select) { proxySelect(select, section); syncChips(select); }
        );
      });

      /* Collect the chosen value(s) and whether anything is selectable. */
      var chosen = [];
      var selectable = false;

      visible.forEach(function (box) {
        controls(box).forEach(function (c) {
          if (!c.disabled) selectable = true;
        });

        Array.prototype.forEach.call(
          box.querySelectorAll('.ymq-option-rc-input:checked'),
          function (input) {
            var v = (input.value || '').trim();
            if (v) chosen.push(v);
          }
        );

        Array.prototype.forEach.call(
          box.querySelectorAll('select.ymq-option-native-select'),
          function (select) {
            syncChips(select);
            if (select.value) chosen.push(select.value.trim());
          }
        );
      });

      var stateName = chosen.length ? 'complete' : (selectable ? 'active' : 'awaiting');
      section.node.setAttribute('data-ac-state', stateName);
      section.node.querySelector('.ac-step__value').textContent = chosen.join(' · ');

      var hint = section.node.querySelector('.ac-step__hint');
      hint.textContent = stateName === 'awaiting' ? 'Choose ' + firstTitle + ' first' : '';

      /* Number only the sections actually on the page. */
      shown += 1;
      section.node.querySelector('.ac-step__num').textContent = pad(shown);
    });
  }

  /* Deliberately a timer, not requestAnimationFrame: rAF does not fire while
     the tab is hidden, which would leave the sections showing a stale state
     for anyone who chooses a size, switches tab and comes back. */
  function queueSync() {
    if (syncQueued) return;
    syncQueued = true;
    window.setTimeout(function () {
      syncQueued = false;
      try { sync(); } catch (e) { /* never break the page over presentation */ }
    }, 16);
  }

  /* ------------------------------------------------------------------ build */

  function build() {
    var list = boxes(document).filter(function (b) { return !b.closest('.' + SHELL); });
    if (!list.length) return false;

    var restore = list.map(function (b) {
      return { box: b, parent: b.parentNode, next: b.nextSibling };
    });

    try {
      state = render(list);
      built = true;
      sync();
      /* Mark the form so CSS can order it options → quantity → add to
         basket, only on pages where the configurator actually runs. */
      var form = state.shell.closest('.product-form');
      if (form) form.classList.add('ac-form');
    } catch (e) {
      /* Put the app's own layout back exactly as it was, and stand down. */
      restore.forEach(function (r) {
        if (r.parent) r.parent.insertBefore(r.box, r.next);
        r.box.classList.remove('ac-reveal');
      });
      if (state && state.shell && state.shell.parentNode) {
        state.shell.parentNode.removeChild(state.shell);
      }
      state = null;
      built = false;
      return false;
    }

    /* The app re-renders its groups when a dependency changes: keep in step. */
    if (observer) observer.disconnect();
    observer = new MutationObserver(function (records) {
      var fresh = false;
      records.forEach(function (r) {
        Array.prototype.forEach.call(r.addedNodes || [], function (n) {
          if (n.nodeType === 1 && (n.matches && n.matches(BOX)) && !n.closest('.' + SHELL)) fresh = true;
        });
      });
      if (fresh) { built = false; window.setTimeout(start, 120); }
      else queueSync();
    });

    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'disabled']
    });

    /* Selection lives on the inputs' `checked` property, which no mutation
       record reports, so listen for the events themselves — at document level,
       so a change the app makes on its own is picked up too. */
    document.addEventListener('change', queueSync, true);
    document.addEventListener('click', queueSync, true);
    document.addEventListener('visibilitychange', queueSync);

    return true;
  }

  /* ------------------------------------------------------------------ start */

  var attempts = 0;

  function start() {
    if (built) { queueSync(); return; }
    if (build()) return;
    attempts += 1;
    if (attempts < 60) window.setTimeout(start, 250);
  }

  function boot() {
    if (!document.body) return;
    /* Product pages only, and only where the app actually renders options. */
    if (!/template-product/.test(document.body.className)) return;
    start();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function () {
    built = false;
    attempts = 0;
    start();
  });
})();
